import { Loader2, Search } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface SearchInputProps
  extends Omit<React.ComponentProps<'input'>, 'size' | 'onChange' | 'value'> {
  value: string
  onValueChange: (value: string) => void
  /** `default` = h-9 list filter (Documents/Identity); `lg` = h-12 hero bar. */
  inputSize?: 'default' | 'lg'
  /** Show a trailing spinner (e.g. while a query is in flight). */
  loading?: boolean
  /** Trailing action, e.g. an "Ask agent" button on the hero search. */
  action?: React.ReactNode
  /** Trailing adornment placed before the action (e.g. a result count). */
  trailing?: React.ReactNode
  /** Classes for the bordered container (e.g. `flex-1`). */
  containerClassName?: string
}

/**
 * Search field shared across the case pages. Visual reference is the Documents
 * search: `bg-card`, a simple border (no shadow), and a primary focus ring.
 * `inputSize="lg"` + `action` cover hero bars that need more height and a CTA.
 */
export function SearchInput({
  value,
  onValueChange,
  inputSize = 'default',
  loading,
  action,
  trailing,
  className,
  containerClassName,
  ...inputProps
}: SearchInputProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-border bg-card px-3 transition-colors focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15',
        inputSize === 'lg' ? 'h-12' : 'h-9',
        containerClassName
      )}
    >
      <Search className="size-4 shrink-0 text-muted-foreground" />
      <input
        type="text"
        value={value}
        onChange={e => onValueChange(e.target.value)}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent text-base outline-none md:text-sm placeholder:text-muted-foreground/50',
          className
        )}
        {...inputProps}
      />
      {loading && <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground/70" />}
      {trailing}
      {action}
    </div>
  )
}
